import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useShopPeople } from "@/lib/people/use-shop-people";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { useSuperAdminAccessLogs } from "@babackapp/private-api-client/client/use-super-admin-access-logs";
import type { SuperAdminAccessLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { DataTable } from "@babackapp/ui/components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

const PAGE_SIZE = 50;

/**
 * The shop's journal of Baback team access (BAB-3035): every write and data
 * export, and the first visit of each day. Kept a year by the backend.
 */
export default function BabackAccessLogPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [page, setPage] = useState(0);
  const people = useShopPeople();

  // ADMIN and OWNER only, like the route; a super admin resolves as ADMIN.
  const canRead = useViewerHasAtLeastAdminRights();

  const {
    data: logs = [],
    isLoading,
    error,
  } = useSuperAdminAccessLogs({
    shopId: shop.id,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    isEnabled: canRead,
  });

  const nameById = useMemo(
    () => new Map(people.map((person) => [person.userId, person.displayName])),
    [people],
  );

  const columns = useMemo<ColumnDef<SuperAdminAccessLogDto>[]>(
    () => [
      {
        id: "date",
        header: t("babackAccess.columns.date"),
        size: 170,
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {format(new Date(row.original.createdAt), "dd/MM/yyyy HH:mm")}
          </span>
        ),
      },
      {
        id: "person",
        header: t("babackAccess.columns.person"),
        size: 260,
        cell: ({ row }) => (
          <span className="text-sm">
            {nameById.get(row.original.userId) ??
              `${row.original.userEmail} (${t("superAdmin.supportLabel")})`}
          </span>
        ),
      },
      {
        id: "kind",
        header: t("babackAccess.columns.action"),
        size: 160,
        cell: ({ row }) => (
          <Badge
            variant={row.original.kind === "ACCESS" ? "secondary" : "default"}
          >
            {t(`babackAccess.kind.${row.original.kind}`)}
          </Badge>
        ),
      },
      {
        id: "detail",
        header: t("babackAccess.columns.detail"),
        // A team change says what it did; the route alone hides who was
        // added or named owner.
        cell: ({ row }) => (
          <div className="flex flex-col gap-0.5">
            {row.original.detail && (
              <span className="text-sm">{row.original.detail}</span>
            )}
            <span className="font-mono text-xs text-muted-foreground">
              {row.original.method} {row.original.route}
            </span>
          </div>
        ),
      },
    ],
    [nameById, t],
  );

  return (
    <div className="w-full flex justify-center pt-16 px-4">
      <div className="w-full flex-1 max-w-[1100px] flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t("babackAccess.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("babackAccess.description")}
          </p>
        </div>

        {!canRead || error ? (
          <p className="text-sm text-muted-foreground">
            {t(canRead ? "babackAccess.error" : "babackAccess.forbidden")}
          </p>
        ) : !isLoading && logs.length === 0 && page === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("babackAccess.empty")}
          </p>
        ) : (
          <DataTable columns={columns} data={logs} isLoading={isLoading} />
        )}

        <div className="flex items-center justify-end gap-2 pb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className="w-4 h-4" />
            {t("misc.previous")}
          </Button>
          <span className="text-sm text-muted-foreground">{page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={logs.length < PAGE_SIZE}
          >
            {t("misc.next")}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
